import React from 'react';
import Icon from '../../../components/AppIcon';

const ComparisonSection = () => {

  const compareRows = [
    {
      label: 'Levensduur',
      grpGraphene: '20.000 laadcycli',
      market: '4.000–6.000 cycli',
      marketBad: true,
    },
    {
      label: 'Capaciteit',
      grpGraphene: '11,2 kWh',
      market: 'Wisselend per merk',
      marketBad: false,
    },
    {
      label: 'Installatie',
      grpGraphene: 'Compleet geïnstalleerd, één aanspreekpunt',
      market: 'Vaak losse onderdelen, meerdere partijen',
      marketBad: true,
    },
    {
      label: 'Veiligheidstests',
      grpGraphene: 'UN 38.3 + Kiwa Primara (onafhankelijk)',
      market: 'Per merk verschillend, soms alleen eigen verklaring',
      marketBad: true,
    },
    {
      label: 'Back-up bij stroomuitval',
      grpGraphene: 'Automatisch, <10 ms schakelsnelheid',
      market: '10–20 ms (wisselend per merk)',
      marketBad: false,
    },
    {
      label: 'Garantie',
      grpGraphene: 'Tot 15 jaar (stapelbaar model)',
      market: '5–10 jaar (model-afhankelijk)',
      marketBad: false,
    },
    {
      label: 'Nederlandse support',
      grpGraphene: 'Eigen portaal + NL klantenservice',
      market: 'Wisselend, vaak buitenlands',
      marketBad: true,
    },
  ];

  return (
    <section id="vergelijking" className="py-16 lg:py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="inline-flex items-center space-x-2 text-primary bg-primary/10 px-3 py-1.5 rounded-full text-xs font-heading font-semibold uppercase tracking-wider mb-5">
            <Icon name="BarChart2" size={12} />
            <span>Marktoverzicht</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground leading-tight mb-4">
            Waarom kiezen steeds meer huishoudens voor GRP?
          </h2>
          <p className="font-body text-muted-foreground text-lg leading-relaxed">
            De meeste thuisbatterijen op de markt zijn technisch vergelijkbaar. GRP doet het op drie punten fundamenteel anders — en dat merk je pas als je ze naast elkaar legt.
          </p>
        </div>

        {/* Hero stat cards — GRP Graphene + market */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-10 max-w-2xl mx-auto">
          {/* GRP Graphene card */}
          <div className="relative rounded-2xl p-6 flex flex-col bg-primary text-white shadow-lg shadow-primary/20">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-amber-900 text-xs font-heading font-bold px-3 py-1 rounded-full uppercase tracking-wide whitespace-nowrap">
              Premium keuze
            </div>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 bg-white/20">
              <Icon name="Atom" size={20} className="text-white" />
            </div>
            <div className="text-2xl font-heading font-black mb-0.5 text-white">GRP Graphene</div>
            <div className="text-sm font-heading font-semibold mb-1 text-white/90">11,2 kWh · 20.000 cycli</div>
            <div className="text-xs font-body mb-4 text-white/70">Kiwa gecertificeerd · Tot 15 jaar garantie</div>
            <div className="mt-auto text-xs font-body rounded-lg px-3 py-2 bg-white/15 text-white/90">
              <span className="font-semibold">Markt: </span>4.000–6.000 cycli bij A-merken
            </div>
          </div>

          {/* Market card */}
          <div className="rounded-2xl p-6 flex flex-col bg-white border border-border shadow-sm">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 bg-rose-50">
              <Icon name="ShoppingBag" size={20} className="text-rose-400" />
            </div>
            <div className="text-2xl font-heading font-black mb-0.5 text-foreground">A-merken</div>
            <div className="text-sm font-heading font-semibold mb-1 text-foreground">Gangbare markt</div>
            <div className="text-xs font-body mb-4 text-muted-foreground">Tesla, AlphaESS, Sessy, Sigenergy e.a.</div>
            <div className="mt-auto text-xs font-body rounded-lg px-3 py-2 bg-slate-50 text-muted-foreground">
              <span className="font-semibold">Let op: </span>Vaak losse onderdelen, meerdere partijen, wisselende garanties
            </div>
          </div>
        </div>

        {/* Visual comparison table */}
        <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden mb-8">
          {/* Table header */}
          <div className="grid grid-cols-[1.5fr_1fr_1fr] border-b border-border">
            <div className="px-4 py-4 bg-slate-50">
              <span className="text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider">Vergelijkingspunt</span>
            </div>
            <div className="px-4 py-4 bg-primary/5 border-l border-border">
              <div className="flex items-center space-x-1.5">
                <Icon name="Atom" size={13} className="text-primary" />
                <span className="text-xs font-heading font-bold text-primary uppercase tracking-wider">GRP Graphene</span>
              </div>
            </div>
            <div className="px-4 py-4 bg-slate-50 border-l border-border">
              <span className="text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider">Gangbare A-merken</span>
            </div>
          </div>

          {/* Rows */}
          {compareRows?.map((row, i) => (
            <div
              key={i}
              className={`grid grid-cols-[1.5fr_1fr_1fr] border-b border-border/50 last:border-0 ${i % 2 === 0 ? '' : 'bg-slate-50/40'}`}
            >
              <div className="px-4 py-4 flex items-center">
                <span className="text-sm font-body font-semibold text-foreground">{row?.label}</span>
              </div>
              <div className="px-4 py-4 bg-primary/[0.03] border-l border-border/50 flex items-start space-x-2">
                <Icon name="CheckCircle" size={15} className="text-primary mt-0.5 shrink-0" />
                <span className="text-sm font-body text-foreground">{row?.grpGraphene}</span>
              </div>
              <div className="px-4 py-4 border-l border-border/50 flex items-start space-x-2">
                <Icon
                  name={row?.marketBad ? 'XCircle' : 'Minus'}
                  size={15}
                  className={`mt-0.5 shrink-0 ${row?.marketBad ? 'text-rose-400' : 'text-amber-400'}`}
                />
                <span className="text-sm font-body text-muted-foreground">{row?.market}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom summary banner */}
        <div className="rounded-2xl bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center shrink-0">
            <Icon name="Zap" size={22} className="text-white" />
          </div>
          <div className="flex-1">
            <p className="font-heading font-bold text-foreground text-base mb-1">
              Kortom: GRP levert meer voor minder gedoe
            </p>
            <p className="font-body text-muted-foreground text-sm leading-relaxed">
              Eén partij regelt alles — van advies tot installatie tot service. Geen losse onderdelen, geen onduidelijke garanties, geen buitenlandse helpdesk. Gewoon een batterij die werkt, lang meegaat en onafhankelijk is getest.
            </p>
          </div>
          <a
            href="#contact"
            className="shrink-0 inline-flex items-center space-x-2 bg-primary text-white font-heading font-semibold text-sm px-5 py-3 rounded-xl hover:bg-primary/90 transition-colors"
          >
            <span>Vrijblijvend advies</span>
            <Icon name="ArrowRight" size={16} />
          </a>
        </div>

        <p className="text-xs font-caption text-muted-foreground mt-5 italic">
          * Garantievoorwaarden worden vóór publicatie geverifieerd met GRP Energy. Concurrenten niet bij naam genoemd. Marktwaarden zijn indicatief voor 2026 (referentiemerken: Tesla Powerwall 3, AlphaESS, Sessy, HomeWizard, Zonneplan Nexus, Sigenergy). Prijzen volgen na afstemming.
        </p>
      </div>
    </section>
  );
};

export default ComparisonSection;
